import { Action, Button } from '@op/opds.opux';
import { Modal } from '@op/opds.opux-modal';
import { type FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CoverageInfoContent from './CoverageInfoContent';
import styles from './CoverageSelector.module.scss';
import {
  AnalyticsPhases,
  insuranceProductName,
  trackAction,
} from '../../../api/analytics-api';
import { APP_ELEMENT_ID } from '../../../constants';
import { getInitialState } from '../../../utils';
import { type Coverage } from 'dto/marketableProduct';

interface Props {
  coverage: Coverage;
}

const CoverageInfoModal: FC<Props> = ({ coverage }) => {
  const { authenticatedCustomer } = getInitialState();
  const { t } = useTranslation(['individualLife']);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const closedByFooterButton = useRef(false);
  const hasBeenOpened = useRef(false);

  const actionText = t('coverageProduct.readMore');
  const ariaLabel = `${t('coverageProduct.readMore')}: ${t(
    `coverageProduct.${coverage.coverageProduct}.name`
  )}`;

  const handleModalOpen = () => {
    setIsModalOpen(true);
    hasBeenOpened.current = true;
    trackAction(
      `${insuranceProductName[coverage.coverageProduct]} opened`,
      'modal',
      authenticatedCustomer
        ? AnalyticsPhases.BENEFICIARIES_AND_DOCUMENTS
        : AnalyticsPhases.PRICE
    );
  };

  return (
    <>
      <Action
        onClick={handleModalOpen}
        data-testid={`${coverage.coverageProduct}-info-modal-trigger`}
        aria-label={ariaLabel}
        className={styles.coverageInfoAction}>
        {actionText}
      </Action>
      <Modal
        appElement={document.getElementById(APP_ELEMENT_ID) ?? undefined}
        showCloseButton={true}
        isOpen={isModalOpen}
        title={t(`coverageProduct.${coverage.coverageProduct}.name`)}
        titleProps={{
          'data-testid': `${coverage.coverageProduct}-info-modal-title`,
        }}
        onUpdate={({ open }: { open: boolean }) => {
          if (!open && hasBeenOpened.current) {
            const buttonName = closedByFooterButton.current ? 'SULJE' : 'X';
            trackAction(
              `${insuranceProductName[coverage.coverageProduct]} closed from ${buttonName} button`,
              'modal',
              authenticatedCustomer
                ? AnalyticsPhases.BENEFICIARIES_AND_DOCUMENTS
                : AnalyticsPhases.PRICE
            );
            closedByFooterButton.current = false;
          }
          setIsModalOpen(open);
        }}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}>
            <Button
              compact
              onClick={() => {
                closedByFooterButton.current = true;
                setIsModalOpen(false);
              }}
              data-testid={`${coverage.coverageProduct}-info-modal-close-button`}>
              {t('coverageProduct.closeModal')}
            </Button>
          </div>
        }>
        <CoverageInfoContent coverage={coverage} />
      </Modal>
    </>
  );
};

export default CoverageInfoModal;
